import { useMemo } from "react";
import { useLocation } from "react-router-dom";

function parseQuery<T extends Record<string, any>>(search: string, defaultValues: T): T
{
  const params = new URLSearchParams(search);
  const result: any = { ...defaultValues };
  for(const key in defaultValues)
  {
    if(params.has(key))
    {
      const value = params.get(key);
      // Handle union types like number | null, boolean | null, etc.
      const def = defaultValues[key];
      if(typeof def === "number" || (def === null && /^-?\d+(\.\d+)?$/.test(value!)))
      {
        result[key] = value !== null ? Number(value) : undefined;
      } else if(typeof def === "boolean" || (def === null && (value === "true" || value === "false")))
      {
        result[key] = value === "true";
      } else if(def === null)
      {
        // Try to infer nullables: if value is empty string, treat as null
        result[key] = value === null || value === "" ? null : value;
      } else
      {
        result[key] = value;
      }
    }
  }
  return result;
}

/**
 * useUrlQuery - React hook to get typed query params from the URL
 * @param defaultValues An object with the keys and default values/types you want to extract from the query string
 * @returns An object with the same keys, parsed from the URL query string
 */
export default function useUrlQuery<T extends Record<string, any>>(defaultValues: T): T
{
  const location = useLocation();
  return useMemo(() => parseQuery(location.search, defaultValues), [location.search, defaultValues]);
}
